import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import ProfileBanner from "../../../components/Dashboard/ProfileBanner";
import ItemProgressbar from "../../../components/Dashboard/Participant/ItemProgressbar";
import ParticipantCampaignTracking from "../../../components/Dashboard/Participant/CampaignTacking";
import ParticipantProgressCard from "../../../components/Dashboard/Participant/ProgressCard";
import ParticipantStoreActions from "../../../components/Dashboard/Participant/StoreActions";
import ParticipantOrderDisplay from "../../../components/Dashboard/Participant/ParticipantOrderDisplay";
import BuyoutComponent from "./Buyout";
import config from "../../../../src/config";
import "./Participant.css";
import "../../../constants.css";
import {
  parseParticipantID,
  getCurrentlyLoggedInUser,
  InvalidFundraiserKey,
  encodeQueryParameter,
} from "../../../CommonFunctions";
import { LoggedInAccount } from "../../../Interfaces/AccountUsers";
import { FundraiserParticipantShop } from "../../../Interfaces/FundraiserRole";
import fetchWithAuth from "../../../services/fetchWithAuth";

import {
  CustomerSale,
  ParticipantSales,
  SalesData,
} from "../../../Interfaces/Dashboards";
import UnauthroizedParticipant from "./UnauthorizedParticipant";
import ParticipantCodeDisplay from "./ParticipantDisplayCode";

const ParticipantDashboard = () => {
  const BASE_API = config.API_BASE_URL;
  const BASE_SITE = config.REACT_APP_SITE_URL;
  const { linkExtension } = useParams();
  const [loggedInAccount, setLoggedInAccount] =
    useState<LoggedInAccount | null>(null);
  const [copyMessage, setCopyMessage] = useState<string>(""); // State for the copy message

  const [storeLink, setStoreLink] = useState<string>("");
  const [participantShop, setParticipantShop] =
    useState<FundraiserParticipantShop | null>(null);

  const [participantID, setParticipantID] = useState<number | null>(null);
  const [participantItemsAmountSold, setParticipantItemsAmountSold] =
    useState<ParticipantSales | null>(null);
  const [customerSales, setCustomerSales] = useState<CustomerSale[] | null>(
    null
  );
  const [errorMessage, setErrorMessage] = useState("");
  const [loadedData, setLoadedData] = useState(0);

  useEffect(() => {
    const fetchLoggedInUser = async () => {
      try {
        const user = await getCurrentlyLoggedInUser();
        setLoggedInAccount(user);
      } catch (error) {
        // console.error("Failed to fetch logged-in user:", error);
        navigate("/login");
      }
    };

    fetchLoggedInUser();
    document.title = "Participant Dashboard";
  }, []);

  useEffect(() => {
    const fetchParticipantData = () => {
      setStoreLink(`participant-shop/part-id/${linkExtension}`);
      try {
        const parsedID = parseParticipantID(linkExtension || "");
        setParticipantID(parsedID);
      } catch (error) {
        if (error instanceof InvalidFundraiserKey) {
          setErrorMessage(
            "Incorrect participant ID used, navigating back to dashboard..."
          );
          setTimeout(() => {
            navigate("/dashboard/user");
          }, 5000);
        } else {
          console.error("An unexpected error occurred:", error);
        }
      }
    };

    fetchParticipantData();
  }, [linkExtension]);

  useEffect(() => {
    const fetchData = async () => {
      if (!participantID) {
        // console.error("participantID is null or undefined, skipping fetch.");
        return;
      }
      try {
        const response = await fetchWithAuth(
          `${BASE_API}sales/participant_store_info/${participantID}`
        );
        const result: SalesData = await response.json();
        setParticipantItemsAmountSold(result.participant_sales);
        setCustomerSales(result.customer_sales);
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
        setParticipantItemsAmountSold(null);
        setLoadedData(1);
      }
    };
    const fetchParticipantData = async () => {
      if (!participantID) {
        // console.error("participantID is null or undefined, skipping fetch.");
        return;
      }
      try {
        const response = await fetchWithAuth(
          `${BASE_API}fundraiser-role/store-info/${participantID}/`
        );
        const result: FundraiserParticipantShop = await response.json();
        setParticipantShop(result);
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
        setParticipantShop(null);
        setLoadedData(1);
      }
    };

    fetchData();
    fetchParticipantData();
  }, [participantID, BASE_API]); // Make sure this effect runs when `participantID` or `BASE_API` changes

  const navigate = useNavigate();

  const handleViewStore = () => {
    navigate(`/${storeLink}`);
  };

  const handleAddMoreEmails = () => {
    const participantsQuery = encodeQueryParameter({
      participants: `${participantID}`,
    });

    navigate(`/fundraiser/participant/add-contacts/${participantsQuery}`);
  };

  const handleShareStore = () => {
    navigator.clipboard.writeText(`${BASE_SITE}${storeLink}`);
    setCopyMessage("Store link has been copied.!"); // Show the copy message

    // Hide the message after 3 seconds
    setTimeout(() => {
      setCopyMessage("");
    }, 3000);
  };

  useEffect(() => {
    if (loggedInAccount && participantShop) {
      setLoadedData(2);
    }
  }, [loggedInAccount, participantShop]);

  // check to ensure the account profile being loaded is attached to the account signed in
  const accountProfilePartOfAccount =
    (loggedInAccount &&
      typeof participantShop?.account_profile_id === "number" &&
      loggedInAccount.account_profiles.includes(
        participantShop.account_profile_id
      )) ||
    !loadedData;

  if (errorMessage || loadedData === 1) {
    return (
      <div className="participant-dashboard-error-warning">{errorMessage}</div>
    );
  }

  if (!loadedData) {
    return <div>Loading...</div>;
  }

  return accountProfilePartOfAccount ? (
    <div className="participant-dashboard-wrapper">
      <div className="participant-dashboard-banner">
        {participantShop && (
          <ProfileBanner
            name={`${participantShop.full_name}`}
            role="Participant"
            profile_pic={`${config.BACKEND_BASE_IMAGE_LOCATION}${participantShop.profile_image_url}`}
          />
        )}
      </div>
      <div className="participant-dashboard-content">
        <div className="participant-dashboard-main">
          {participantShop && (
            <ParticipantCodeDisplay code={participantShop.participant_code} />
          )}
          {participantShop && participantShop.buyout > 0 && participantID && (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <BuyoutComponent
                originalBuyout={participantShop.buyout}
                profitEarnedSoFar={participantShop.amount_raised}
                participantID={participantID}
                has_boughtout={participantShop.has_boughtout}
                fundraiser_end_date={participantShop.fundraiser_end_date}
              />
            </div>
          )}
          <div className="flex justify-center items-center p-6">
            <ParticipantProgressCard
              personalAmount={`$${
                participantItemsAmountSold?.self_total_money || 0
              }`}
              personalItems={`${
                participantItemsAmountSold?.self_total_items || 0
              }`}
              teamAmount={`$${
                participantItemsAmountSold?.team_total_money || 0
              }`}
              teamItems={`${participantItemsAmountSold?.team_total_items || 0}`}
            />
          </div>
          <div className="participant-dashboard-store-actions-container">
            {participantShop && (
              <ParticipantStoreActions
                onViewStore={handleViewStore}
                onShareStore={handleShareStore}
                onAddMoreEmails={handleAddMoreEmails}
                copyMessage={copyMessage}
                participantID={participantShop.participant_code}
                participantName={participantShop.full_name}
                organizationName={participantShop.organization_name}
              />
            )}
          </div>
          <div>
            {participantID !== null && participantID !== -1 && (
              <ParticipantOrderDisplay customerSales={customerSales} />
            )}
          </div>
        </div>
      </div>
    </div>
  ) : (
    <UnauthroizedParticipant />
  );
};

export default ParticipantDashboard;
